import { Link } from 'react-router';
import {
  autoPayAsks,
  cardSummary,
  dueList,
  loanState,
  type PayeeLike,
  type PaymentsSnapshot,
} from '@tahi/core';
import { useUsable } from '../../../data/hooks';
import { MenuCard, Money } from '../../../ui';
import { cn } from '../../../ui/cn';
import { spokenPeso } from '../../../ui/spokenPeso';
import { CARD, FOCUS, H1, LABEL, SCREEN } from '../../spending/styles';
import { AutoPayAsk } from '../components/AutoPayAsk';
import { DueRow } from '../components/DueRow';
import { copy } from '../copy';
import { dayShort, interestText, peso, scheduleText } from '../format';
import { PAYMENTS_BASE, useInstallmentCatchUp, usePaymentsSnapshot, useToday } from '../hooks';

const KINDS = ['bill', 'utility', 'card', 'loan'] as const;

function PayeeRow({
  to,
  name,
  sub,
  minor,
}: {
  to: string;
  name: string;
  sub: string;
  minor: number | null;
}) {
  return (
    <li>
      <Link
        to={to}
        className={cn(
          'flex min-h-14 items-center justify-between gap-3 rounded-2xl px-2 py-2 hover:bg-secondary',
          FOCUS,
        )}
        aria-label={`${name}, ${sub}${minor !== null ? `, ${spokenPeso(minor)}` : ''}`}
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-fg">{name}</span>
          <span className="truncate text-sm text-muted">{sub}</span>
        </span>
        {minor !== null ? (
          <Money
            minor={minor}
            cents="auto"
            animate={false}
            className="shrink-0 text-base text-fg"
          />
        ) : null}
      </Link>
    </li>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className={`${CARD} flex flex-col gap-1`}>
      <h2 className={LABEL}>{title}</h2>
      <ul className="flex flex-col">{children}</ul>
    </section>
  );
}

function rows(snap: PaymentsSnapshot, today: string, payees: PayeeLike[]) {
  return payees.map((p) => {
    const to = `${PAYMENTS_BASE}/p/${p.id}`;
    if (p.kind === 'card') {
      const s = cardSummary(snap, p, today);
      const sub = s.nextDue
        ? `Due ${dayShort(s.nextDue.dueDate)} · ${peso(s.nextDue.remainingMinor)}`
        : scheduleText(p);
      return <PayeeRow key={p.id} to={to} name={p.name} sub={sub} minor={s.balanceMinor} />;
    }
    if (p.kind === 'loan') {
      const st = loanState(snap, p, today);
      const sub =
        st.type === 'flexible'
          ? interestText(p.interestBp)
          : copy.paymentsLeft(st.paymentsLeft ?? 0);
      const who = p.lender && p.lender !== p.name ? `${p.lender} · ` : '';
      return (
        <PayeeRow key={p.id} to={to} name={p.name} sub={`${who}${sub}`} minor={st.balanceMinor} />
      );
    }
    return (
      <PayeeRow
        key={p.id}
        to={to}
        name={p.name}
        sub={`${scheduleText(p)}${p.kind === 'utility' ? ` · ${copy.estimated} ${peso(p.amountMinor ?? 0)}` : ''}`}
        minor={p.kind === 'bill' ? (p.amountMinor ?? 0) : null}
      />
    );
  });
}

/** Payments home: questions for auto-debits, "Payments due · next 30 days", then every payee. */
export function PaymentsDashboard() {
  const snap = usePaymentsSnapshot();
  const today = useToday();
  const usable = useUsable();
  useInstallmentCatchUp();
  if (!snap) return <p className="text-muted">{copy.loading}</p>;

  const list = dueList(snap, today);
  const asks = autoPayAsks(list);
  const payees = snap.payees.filter((p) => p.deletedAt === null);
  const bills = payees.filter((p) => p.kind === 'bill' || p.kind === 'utility');
  const cards = payees.filter((p) => p.kind === 'card');
  const loans = payees.filter((p) => p.kind === 'loan');
  const totalDue = list.reduce((s, d) => s + d.remainingMinor, 0);

  return (
    <div className={SCREEN}>
      <h1 className={H1}>{copy.title}</h1>
      <nav aria-label={copy.add} className="flex flex-wrap gap-2">
        {KINDS.map((k) => (
          <Link
            key={k}
            to={`${PAYMENTS_BASE}/new?kind=${k}`}
            className={`inline-flex min-h-11 items-center rounded-full border border-accent bg-secondary px-4 text-sm font-medium text-fg hover:bg-[var(--hover-strong)] ${FOCUS}`}
          >
            {copy.addKinds[k]}
          </Link>
        ))}
      </nav>

      {payees.length === 0 ? (
        <section className={`${CARD} flex flex-col gap-2`}>
          <p className="text-lg text-fg">{copy.emptyTitle}</p>
          <p className="text-sm text-muted">{copy.emptyBody}</p>
        </section>
      ) : null}

      {asks.map((d) => (
        <section
          key={d.id}
          className={CARD}
          aria-label={copy.autoPayAsk(d.payeeName, d.remainingMinor)}
        >
          <AutoPayAsk due={d} />
        </section>
      ))}

      {payees.length > 0 ? (
        <section aria-label={copy.dueTitle} className={`${CARD} flex flex-col gap-1`}>
          <div className="flex items-baseline justify-between gap-2">
            <h2 className={LABEL}>{copy.dueTitle}</h2>
            {totalDue > 0 ? (
              <Money minor={totalDue} cents="auto" animate={false} className="text-sm text-muted" />
            ) : null}
          </div>
          {list.length === 0 ? (
            <p className="py-2 text-sm text-muted">{copy.nothingDue}</p>
          ) : (
            <ul className="flex flex-col">
              {list.map((d) => (
                <DueRow key={d.id} due={d} />
              ))}
            </ul>
          )}
        </section>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {bills.length > 0 ? (
          <Section title={copy.billsTitle}>{rows(snap, today, bills)}</Section>
        ) : null}
        {cards.length > 0 ? (
          <Section title={copy.cardsTitle}>{rows(snap, today, cards)}</Section>
        ) : null}
        {loans.length > 0 ? (
          <Section title={copy.loansTitle}>{rows(snap, today, loans)}</Section>
        ) : null}
      </div>
      <MenuCard
        label={copy.more}
        items={[
          payees.length > 0 &&
            usable('budget') && {
              to: '/m/budget/cashflow',
              label: copy.whatsLeftPerCutoff,
              icon: 'cashflow',
            },
          {
            to: `${PAYMENTS_BASE}/settings`,
            label: copy.settings,
            icon: 'settings',
          },
        ]}
      />
    </div>
  );
}
